/**
 * CRM bridge — deliver the completed lead to GHL via the Cloudflare Worker.
 *
 * The SPA never calls GHL directly. It POSTs to our Worker, which does bot
 * filtering, the GHL contact upsert, Meta CAPI, and server-side retry.
 * PLAN.md §10. This module builds the payload and performs the browser-side
 * POST with a short retry (the Worker owns the durable retry).
 */
import type { Answers, FunnelConfig, Outcome } from "../types";

/**
 * One human-readable Q&A pair for a notification surface (Slack/SMS). Derived
 * from the funnel config at submit time so the Worker can render any set of
 * questions without hardcoding field names. Adding/changing a question in
 * funnel.config.json automatically flows through to notifications.
 */
export interface HumanAnswer {
  /** Source field id (stable identifier). */
  field_id: string;
  /** The question as shown to the user (field.label, or step heading fallback). */
  question_label: string;
  /** The answer as shown to the user. For single/multi_select: the option label
   *  for the selected value(s). For text/number/state/etc: the raw value. */
  answer_label: string;
  /** Raw value (the same shape stored in raw_answers). Lets the Worker do its
   *  own logic if needed without re-resolving labels. */
  raw_value: string | string[] | number | boolean | null;
}

export interface LeadPayload {
  funnel_id: string;
  session_id: string;
  /** Client-generated idempotency key — dedups double-submits / retries. */
  submission_id: string;
  /** Whether this is a final submit or an early partial capture. */
  partial: boolean;
  /** Answers keyed by GHL custom-field key (only fields with `ghl_field`). */
  ghl_fields: Record<string, string | string[] | number | boolean>;
  /** Raw answers keyed by field id — full fidelity for analytics/debug. */
  raw_answers: Answers;
  /** Question/answer pairs in human-readable form, ordered as they appear in
   *  the funnel. Drives Slack + SMS notification rendering without hardcoding
   *  field names on the Worker. See buildHumanAnswers() for filtering rules. */
  human_answers: HumanAnswer[];
  outcome: Outcome;
  /** TrustedForm certificate URL, when captured. */
  trustedform_cert?: string;
  /** Cloudflare Turnstile token for bot verification. */
  turnstile_token?: string;
  /** Inbound URL params (utm_*, gclid, fbclid, etc.). */
  url_params: Record<string, string>;
  page_url: string;
}

/** Map collected answers onto their GHL custom-field keys. */
export function buildGhlFields(
  config: FunnelConfig,
  answers: Answers,
): Record<string, string | string[] | number | boolean> {
  const out: Record<string, string | string[] | number | boolean> = {};
  for (const step of config.steps) {
    for (const field of step.fields) {
      if (!field.ghl_field) continue;
      const value = answers[field.id];
      if (value !== undefined && value !== "") out[field.ghl_field] = value;
    }
  }
  return out;
}

/**
 * Build the human-readable question/answer list sent to Slack + SMS via
 * `LeadPayload.human_answers`. Walks the funnel config in order, resolving
 * each answer to a (question_label, answer_label) pair.
 *
 * Default-exclude rules — fields that already render in other surfaces or
 * carry no useful Q&A content:
 *   - type === "consent_checkbox"     (compliance section)
 *   - type === "text"|"email"|"tel"   (PII; appear in identity/contact lines)
 *   - type === "zip"                  (in identity line)
 *   - type === "state"                (in identity line)
 *   - type === "textarea"             (rendered as standalone "What Happened")
 *   - type === "hidden"               (not user-visible)
 *   - field.omit_from_notifications   (explicit opt-out per type signature)
 *
 * Included field types: single_select, multi_select, number, date.
 * Adding a new question to funnel.config.json automatically flows through —
 * no Worker code change needed.
 */
export function buildHumanAnswers(
  config: FunnelConfig,
  answers: Answers,
): HumanAnswer[] {
  const out: HumanAnswer[] = [];
  const SKIP_TYPES = new Set(["consent_checkbox", "text", "email", "tel", "zip", "state", "textarea", "hidden"]);
  for (const step of config.steps) {
    for (const field of step.fields) {
      if (SKIP_TYPES.has(field.type)) continue;
      if (field.omit_from_notifications === true) continue;
      const raw = answers[field.id];
      if (raw === undefined || raw === null || raw === "") continue;

      // Question label: prefer field.label (text/state/number questions),
      // fall back to the step's H2 heading block (single_select questions
      // where the heading IS the question), final fallback to field.id.
      const headingBlock = (step.blocks ?? []).find(
        (b) => b.type === "heading" && (b.level === 2 || b.level === undefined),
      );
      const stepHeading = headingBlock && headingBlock.type === "heading" ? headingBlock.text : undefined;
      const question_label = field.label ?? stepHeading ?? field.id;
      let answer_label: string;

      if (field.type === "single_select") {
        const opt = field.options.find((o) => o.value === raw);
        // Prefer the option's notification_label (rewritten for standalone
        // payload clarity) over the user-facing label.
        answer_label = opt?.notification_label ?? opt?.label ?? String(raw);
      } else if (field.type === "multi_select" && Array.isArray(raw)) {
        const labels = raw.map((v) => {
          const opt = field.options.find((o) => o.value === v);
          return opt?.notification_label ?? opt?.label ?? String(v);
        });
        answer_label = labels.join(", ");
      } else {
        answer_label = String(raw);
      }

      out.push({
        field_id: field.id,
        question_label,
        answer_label,
        raw_value: (raw as string | string[] | number | boolean | null) ?? null,
      });
    }
  }
  return out;
}

export function collectUrlParams(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    new URLSearchParams(window.location.search).forEach((v, k) => {
      out[k] = v;
    });
  } catch {
    /* ignore */
  }
  return out;
}

export interface DeliveryResult {
  ok: boolean;
  status?: number;
  error?: string;
  /** Set when the worker returns HTTP 200 with `data_invalid:true` — Layer 2
   *  validation rejected the lead post-submit (typically because Layer 1
   *  field-blur validation was rate-limited and silently passed). The engine
   *  routes the user back to the contact step with field-level errors. */
  data_invalid?: boolean;
  validation_errors?: { phone?: string; email?: string; zip?: string };
}

const sleep = (ms: number): Promise<void> =>
  new Promise((r) => setTimeout(r, ms));

/**
 * POST the lead to the Worker. Browser-side retry is short (3 attempts) — the
 * Worker owns durable retry once it has accepted the request.
 */
export async function deliverLead(
  workerUrl: string,
  payload: LeadPayload,
  attempts = 3,
): Promise<DeliveryResult> {
  let lastError = "";
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(workerUrl, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true, // survive the tab closing right after submit
      });
      if (res.ok) {
        // Inspect body — the Pages Function returns HTTP 200 with
        // `data_invalid:true` when Layer 2 validation rejects the lead.
        // Without this check, the engine would treat the silent drop as
        // success and show the result page.
        try {
          const body = (await res.clone().json()) as {
            data_invalid?: boolean;
            validation_errors?: { phone?: string; email?: string; zip?: string };
          };
          if (body && body.data_invalid === true) {
            return {
              ok: false,
              status: res.status,
              error: "data_invalid",
              data_invalid: true,
              validation_errors: body.validation_errors ?? {},
            };
          }
        } catch {
          // Non-JSON body — treat as success (back-compat with older Worker
          // versions that returned empty 200).
        }
        return { ok: true, status: res.status };
      }
      // 4xx (except 429) is not retryable — the request itself is bad.
      if (res.status >= 400 && res.status < 500 && res.status !== 429) {
        return { ok: false, status: res.status, error: `HTTP ${res.status}` };
      }
      lastError = `HTTP ${res.status}`;
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
    }
    if (i < attempts - 1) await sleep(400 * 2 ** i);
  }
  return { ok: false, error: lastError };
}

export function newSubmissionId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
